import { useState, useCallback, useEffect } from 'react';
// import { Plus, ArrowLeft, Copy, Trash2 } from "lucide-react"; // optional icons
import './Flow.css';
type FlowId = string | null;
import FlowEditor from './FlowEditor';
import {
  createFlowchart,
  duplicateFlowchart,
  deleteFlowchart,
  listFlowcharts,
  fetchGraph,
  saveGraph,
  type Flowchart,
} from './Flow';
import { ReactFlowProvider } from '@xyflow/react';

type Meta = { name: string; description: string };
export default function FlowView() {
  const [selectedId, setSelectedId] = useState<FlowId>(null);
  const [meta, setMeta] = useState({ name: '', description: '' });
  const [isCreating, setIsCreating] = useState(false);

  async function handleCreate() {
    setIsCreating(true);
    try {
      const fc = await createFlowchart();
      // optionally create an empty graph
      await saveGraph(fc.id, 'Untitled Flow', '', {
        nodes: [],
        edges: [],
        replace: true,
      });
      setSelectedId(fc.id);
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDuplicate(id: string) {
    const fc = await duplicateFlowchart(id);
    setSelectedId(fc.id);
  }

  async function handleDelete(id: string) {
    await deleteFlowchart(id);
    if (selectedId === id) setSelectedId(null);
  }

  const onMetaChange = useCallback((patch: Partial<Meta>): void => {
    setMeta((m) => ({ ...m, ...patch }));
  }, []);

  const onSelect = useCallback((id: string) => {
    setSelectedId(id);
    setMeta({ name: '', description: '' });
  }, []);

  return (
    <div className="p-4 h-screen w-screen">
      {/* Top bar */}
      <div className="mb-4 flex items-center justify-between gap-2">
        {selectedId ? (
          <div className="flex">
            <div className="flex items-center gap-2">
              <button
                className="rounded-lg border px-3 py-2 hover:bg-blue-700"
                onClick={() => setSelectedId(null)}
                aria-label="Back to list"
                title="Back to list"
              >
                {/* <ArrowLeft className="inline h-4 w-4 mr-1" /> */}
                Back
              </button>

              <button
                className="rounded-lg border px-3 py-2 hover:bg-blue-700"
                onClick={() => selectedId && handleDuplicate(selectedId)}
                aria-label="Duplicate flowchart"
                title="Duplicate"
              >
                {/* <Copy className="inline h-4 w-4 mr-1" /> */}
                Duplicate
              </button>

              <button
                className="rounded-lg border px-3 py-2 text-red-600 hover:bg-red-50"
                onClick={() => selectedId && handleDelete(selectedId)}
                aria-label="Delete flowchart"
                title="Delete"
              >
                {/* <Trash2 className="inline h-4 w-4 mr-1" /> */}
                Delete
              </button>
            </div>
            {/* <div className="grid items-center gap-2">
              <div className="flex">
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <input
                  className="w-[360px] rounded-lg border px-2 py-1 text-black"
                  value={meta.description}
                  onChange={(e) =>
                    onMetaChange({ description: e.target.value })
                  }
                  placeholder="Flowchart Description"
                />
              </div>
            </div> */}
            <div className="flex">
                <input
                  className="w-[260px]  px-2 py-1 text-white bg-transparent border border-0"
                  value={meta.name}
                  onChange={(e) => onMetaChange({ name: e.target.value })}
                  placeholder="Flowchart Name"
                />
              </div>
          </div>
        ) : (
          <div />
        )}

        {/* "+" New button always visible (disabled while creating) */}
        <button
          onClick={handleCreate}
          disabled={isCreating}
          className="rounded-lg bg-blue-fnn px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          aria-label="Create new flowchart"
          title="Create new flowchart"
        >
          {/* <Plus className="inline h-4 w-4 mr-2" /> */}
          {isCreating ? 'Creating…' : 'New Flowchart'}
        </button>
      </div>

      {/* Main area */}
      {selectedId ? (
        <ReactFlowProvider>
          <FlowEditor
            flowchartId={selectedId}
            name={meta.name}
            description={meta.description}
            onMetaChange={onMetaChange}
            // FlowEditor will call this after it loads graph to hydrate the parent
            onMetaLoaded={(loaded: Meta) => setMeta(loaded)}
          />
        </ReactFlowProvider>
      ) : (
        <FlowchartList
          onSelect={(id) => setSelectedId(id)}
          onCreate={handleCreate}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

type ListProps = {
  onSelect: (id: string) => void;
  onCreate: () => Promise<void> | void;
  onDuplicate: (id: string) => Promise<void> | void;
  onDelete: (id: string) => Promise<void> | void;
};

function FlowchartList({
  onSelect,
  onCreate,
  onDuplicate,
  onDelete,
}: ListProps) {
  const [flows, setFlows] = useState<Flowchart[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await listFlowcharts();
      setFlows(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load flowcharts');
      setFlows([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Delegate to parent handlers and then refresh the list
  const handleCreateClick = async () => {
    try {
      setCreating(true);
      await onCreate();
      await refresh();
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicateClick = async (id: string) => {
    try {
      setBusyId(id);
      await onDuplicate(id);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteClick = async (id: string) => {
    if (!confirm('Delete this flowchart?')) return;
    try {
      setBusyId(id);
      await onDelete(id);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Your Flowcharts</h3>
      </div>

      {error && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {!flows && (
        <div className="animate-pulse rounded border p-3 text-sm text-gray-500">
          Loading…
        </div>
      )}

      {flows && flows.length === 0 && (
        <div className="rounded border p-3 text-sm text-gray-600">
          No flowcharts yet. Click <strong>New Flowchart</strong> to create one.
        </div>
      )}

      <ul className="space-y-2">
        {flows?.map((fc) => (
          <li
            key={fc.id}
            className="flex items-center justify-between rounded border p-3"
          >
            <button
              className="w-full text-left hover:underline"
              onClick={() => onSelect(fc.id)}
              title={fc.description || ''}
            >
              <div className="font-medium">{fc.name}</div>
              <div className="text-xs text-gray-500">
                Updated {new Date(fc.updated_at).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">
                Created by {fc.created_by_username}
              </div>
            </button>

            <div className="ml-3 flex shrink-0 gap-2">
              <button
                className="rounded border px-2 py-1 text-sm hover:bg-blue-fnn hover:text-white disabled:opacity-50"
                onClick={() => handleDuplicateClick(fc.id)}
                disabled={busyId === fc.id}
                aria-label={`Duplicate ${fc.name}`}
                title="Duplicate"
              >
                Duplicate
              </button>
              <button
                className="rounded border px-2 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                onClick={() => handleDeleteClick(fc.id)}
                disabled={busyId === fc.id}
                aria-label={`Delete ${fc.name}`}
                title="Delete"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
